import { cn } from "@lib/utils/cn";
import type { ReactNode } from "react";

/**
 * Column configuration: "auto" responsive default, a fixed count for all
 * breakpoints, or explicit per-breakpoint counts.
 */
export type MetricsGridColumns =
  | "auto"
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | { sm?: 1 | 2 | 3 | 4; md?: 1 | 2 | 3 | 4; lg?: 1 | 2 | 3 | 4 | 5 | 6 };

export type MetricsGridGap = "sm" | "md" | "lg";

export interface MetricsGridProps {
  columns?: MetricsGridColumns;
  gap?: MetricsGridGap;
  className?: string;
  children: ReactNode;
}

const GAP_CLASSES = {
  sm: "gap-3",
  md: "gap-4",
  lg: "gap-6",
} as const;

const AUTO_COLUMNS_CLASSES = "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3";

// Static literals only — dynamic class names would be purged by Tailwind.
const FIXED_COLUMNS_CLASSES: Readonly<Record<number, string>> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
  5: "grid-cols-5",
  6: "grid-cols-6",
};

const RESPONSIVE_COLUMNS_CLASSES = {
  sm: {
    1: "sm:grid-cols-1",
    2: "sm:grid-cols-2",
    3: "sm:grid-cols-3",
    4: "sm:grid-cols-4",
  },
  md: {
    1: "md:grid-cols-1",
    2: "md:grid-cols-2",
    3: "md:grid-cols-3",
    4: "md:grid-cols-4",
  },
  lg: {
    1: "lg:grid-cols-1",
    2: "lg:grid-cols-2",
    3: "lg:grid-cols-3",
    4: "lg:grid-cols-4",
    5: "lg:grid-cols-5",
    6: "lg:grid-cols-6",
  },
} as const;

/**
 * CSS-only entrance stagger (~60ms steps, capped at ~600ms) applied to direct
 * children — children are expected to carry an animation (MetricCard ships
 * animate-fade-in-up); the grid only offsets their start.
 */
const STAGGER_CLASSES = [
  "[&>*]:animate-fade-in-up",
  "[&>*:nth-child(2)]:[animation-delay:60ms]",
  "[&>*:nth-child(3)]:[animation-delay:120ms]",
  "[&>*:nth-child(4)]:[animation-delay:180ms]",
  "[&>*:nth-child(5)]:[animation-delay:240ms]",
  "[&>*:nth-child(6)]:[animation-delay:300ms]",
  "[&>*:nth-child(7)]:[animation-delay:360ms]",
  "[&>*:nth-child(8)]:[animation-delay:420ms]",
  "[&>*:nth-child(9)]:[animation-delay:480ms]",
  "[&>*:nth-child(10)]:[animation-delay:540ms]",
  "[&>*:nth-child(n+11)]:[animation-delay:600ms]",
] as const;

/** Resolves the columns prop to purge-safe static grid classes. */
function resolveColumnClasses(columns: MetricsGridColumns): string {
  if (columns === "auto") return AUTO_COLUMNS_CLASSES;
  if (typeof columns === "number") return FIXED_COLUMNS_CLASSES[columns];
  return [
    columns.sm && RESPONSIVE_COLUMNS_CLASSES.sm[columns.sm],
    columns.md && RESPONSIVE_COLUMNS_CLASSES.md[columns.md],
    columns.lg && RESPONSIVE_COLUMNS_CLASSES.lg[columns.lg],
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * Responsive CSS grid for metric cards with built-in stagger animation.
 * Pure layout brick — no data fetching, no business logic.
 */
export function MetricsGrid({
  columns = "auto",
  gap = "md",
  className,
  children,
}: MetricsGridProps) {
  return (
    <div
      className={cn(
        "grid",
        resolveColumnClasses(columns),
        GAP_CLASSES[gap],
        STAGGER_CLASSES,
        className,
      )}
    >
      {children}
    </div>
  );
}
